/**
 * The shell the two policy pages share: a titled band, an effective date, a
 * contents list, and numbered sections divided by hairlines.
 *
 * Both pages are written as a list of sections rather than as free prose so the
 * numbering, the anchors and the contents list are all derived from one array
 * and cannot fall out of step with each other.
 */

import type { ReactNode } from 'react';

export interface PolicySection {
  /** The anchor, and the id the contents list links to. */
  readonly id: string;
  readonly title: string;
  readonly body: ReactNode;
}

export interface PolicyPageProps {
  readonly eyebrow: string;
  readonly title: string;
  readonly lede: ReactNode;
  /** Rendered under the lede, in the operator's own words. */
  readonly effective: string;
  readonly sections: readonly PolicySection[];
}

/** Two digits, so the rail stays a straight edge past section nine. */
function ordinal(index: number): string {
  return String(index + 1).padStart(2, '0');
}

export function PolicyPage({
  eyebrow,
  title,
  lede,
  effective,
  sections,
}: PolicyPageProps): ReactNode {
  return (
    <div className="policy">
      <div style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--canvas-soft)' }}>
        <div className="inner" style={{ paddingTop: 44, paddingBottom: 40 }}>
          <span className="eyebrow">{eyebrow}</span>
          <h1 style={{ marginTop: 10, fontSize: 36 }}>{title}</h1>
          <p className="lede" style={{ marginTop: 14, maxWidth: '62ch' }}>
            {lede}
          </p>
          <p className="policy-meta">{effective}</p>
        </div>
      </div>

      <section className="section is-last">
        <div className="inner prose">
          <nav aria-label="Contents">
            <ul className="policy-toc">
              {sections.map((section, index) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>
                    <span className="num">{ordinal(index)}</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {sections.map((section, index) => (
            <section key={section.id} id={section.id} className="policy-section">
              <h2>
                <span className="num">{ordinal(index)}</span>
                <span>{section.title}</span>
              </h2>
              {section.body}
            </section>
          ))}
        </div>
      </section>
    </div>
  );
}
